// RouterPingCard.jsx — Hourly router health-check strip.
// Shows the last N pings as a horizontal strip of tiny status blocks
// (green = ok, red = fail). Read like a heart-rate monitor.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Heart, RefreshCw, Loader2, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtHour = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const RouterPingCard = ({ endpoint = '/tenant/gymsales/router-pings', title = 'Hourly connection check', limit = 48 }) => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [pings, setPings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API}${endpoint}?limit=${limit}`, { headers });
      setPings(r.data?.pings || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load connection history.');
    } finally { setLoading(false); }
  }, [headers, endpoint, limit]);

  useEffect(() => { load(); const t = setInterval(() => load({ silent: true }), 60_000); return () => clearInterval(t); }, [load]);

  const summary = useMemo(() => {
    if (!pings.length) return null;
    const ok = pings.filter(p => p.ok).length;
    const total = pings.length;
    const latest = pings[0];
    return { ok, total, uptime: total ? Math.round((ok / total) * 1000) / 10 : 0, latest };
  }, [pings]);

  if (loading && pings.length === 0) {
    return (
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-4 flex items-center justify-center min-h-[100px]" data-testid="router-pings-loading">
        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (err && pings.length === 0) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-rose-800 text-xs flex items-start gap-2" data-testid="router-pings-error">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden" data-testid="router-pings-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 text-slate-900 font-semibold text-sm">
        <Heart className={`w-4 h-4 ${summary?.latest?.ok ? 'text-emerald-600' : 'text-rose-600'}`} />
        {title}
        {summary && (
          <span className="text-[11px] font-normal text-slate-600 ml-2">
            {summary.uptime}% healthy over the last {summary.total} checks
          </span>
        )}
        <button
          type="button"
          onClick={() => load()}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900"
          data-testid="router-pings-refresh"
        >
          <RefreshCw className="w-3 h-3" />Refresh
        </button>
      </div>

      <div className="p-3">
        {pings.length === 0 ? (
          <div className="text-slate-400 text-xs italic px-3 py-4">
            No health checks yet. The first check runs within the hour.
          </div>
        ) : (
          <>
            {/* Strip — newest on the LEFT */}
            <div className="flex flex-wrap gap-1 mb-3" data-testid="router-pings-strip">
              {pings.map((p, i) => (
                <div
                  key={i}
                  className={`w-4 h-6 rounded-sm ${p.ok ? 'bg-emerald-500/70' : 'bg-rose-500/70'} hover:ring-2 hover:ring-white/40 cursor-help`}
                  title={
                    `${fmtHour(p.created_at)}\n` +
                    `${p.ok ? 'Healthy' : 'FAILED'}\n` +
                    `Status: ${p.status_code ?? '—'}${p.latency_ms ? ` · ${Math.round(p.latency_ms)}ms` : ''}\n` +
                    (p.request_id ? `Router ID: ${p.request_id}\n` : '') +
                    (p.error ? `Error: ${p.error}` : '')
                  }
                />
              ))}
            </div>

            {/* Latest check details */}
            {summary?.latest && (
              <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  {summary.latest.ok
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    : <XCircle className="w-4 h-4 text-rose-600" />}
                  <span className="text-slate-900 font-semibold">
                    {summary.latest.ok ? 'Last check: healthy' : 'Last check: failed'}
                  </span>
                  <span className="text-slate-500 ml-auto">{fmtHour(summary.latest.created_at)}</span>
                </div>
                <div className="text-slate-600 space-y-0.5 pl-6">
                  <div>Response time: <span className="text-slate-900 tabular-nums">{Math.round(summary.latest.latency_ms || 0)}ms</span></div>
                  {summary.latest.request_id && (
                    <div>Router request ID: <span className="font-mono text-slate-700 text-[10px]">{summary.latest.request_id}</span></div>
                  )}
                  {!summary.latest.ok && summary.latest.error && (
                    <div className="text-rose-600">Problem: {summary.latest.error}</div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RouterPingCard;
